/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */
import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import cartMasterService from '../../services/cart/cartMasterService';
import cartService from '../../services/cart';
import draftCartService from '../../services/cart/draftCart';
import {
  setCart,
  cartInStoreInitialValue,
  setShippingAddressSteppedCheckout,
  setBillingAddress,
  getTaxEstimation,
  removePromoCode,
  addPromoCode,
  addShippingAddressErrorCode,
  getAvailableDiscountCodeForAbandonedCheckout,
  addLineItems,
  setShowOutOfStockToaster,
  checkLineItemsForOOSVariants,
  removeLineItem,
} from './index.migrated';

import { BILLING_ADDRESS_UNEXPECTED_ERROR } from '../../blocks/Checkout/SteppedCheckout/constants';
import { convertToBuilderNotation } from '../../utils';

export const addPromoError = createAction('addPromoError');
export const removePromoError = createAction('removePromoError');

export const getProductPopularity = createAsyncThunk(
  'cart/getProductPopularity',
  ({ productKeys }) =>
    Promise.all(
      productKeys.map((productKey) =>
        cartService.getProductPopularity({ productKey }),
      ),
    ),
);

export const getWarrantyPlans = createAsyncThunk('cart/getWarrantyPlans', () =>
  cartService.getWarrantyPlans(),
);

export const getAvailableDiscountCodeForAbandonedCarts = createAsyncThunk(
  'cart/getAvailableDiscountCodeForAbandonedCarts',
  () => cartService.getAvailableDiscountCodeForAbandonedCarts(),
);

export const getDraftCart = createAsyncThunk(
  'cart/getDraftCart',
  async (cartId, { rejectWithValue }) => {
    try {
      const cart = await draftCartService.getDraftCart(cartId);
      return { ...cart, isDraft: true };
    } catch (error) {
      return rejectWithValue({
        errorCode: error.apiErrors[0].status,
      });
    }
  },
);

export const addWarrantyPlanToLineItem = createAsyncThunk(
  'cart/addWarrantyPlanToLineItem',
  ({ lineItemId, warrantyPlanId }, { getState }) => {
    const { id } = getState().cart;
    return cartService.addWarrantyPlanToLineItem({
      lineItemId,
      warrantyPlanId,
      cartId: id,
    });
  },
);


export const changeLineItemQuantity = createAsyncThunk(
  'cart/changeLineItemQuantity',
  async ({ lineItemId, quantity }, { getState }) => {
    let oldCart = getState().cart;

    if (!oldCart.id) {
      try {
        // Resolves to null when the user has no cart yet (the service
        // answers 204); keep the empty cart already in the store so the
        // reads below stay safe. A genuine failure still lands in catch.
        oldCart = (await cartService.getActiveCart()) ?? oldCart;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('changeLineItemQuantity getActiveCart error', error);
      }
    }

    const cartId = oldCart.id;
    return cartService.changeLineItemQuantity({ lineItemId, quantity, cartId });
  },
);

export const removeDefaultGifts = createAsyncThunk(
  'cart/removeDefaultGifts',
  async ({ defaultGifts }, { getState }) => {
    let { cart } = getState();

    for (const defaultGift of defaultGifts) {
      const currentLineItem = cart.lineItems.find(
        (lineItem) =>
          lineItem.productId === defaultGift.productId &&
          lineItem.variant.sku === defaultGift.sku,
      );

      if (currentLineItem.quantity === 1) {
        cart = await cartService.removeLineItem({
          lineItemId: currentLineItem.id,
          cartId: cart.id,
        });
      } else {
        cart = await cartService.changeLineItemQuantity({
          lineItemId: currentLineItem.id,
          quantity: currentLineItem.quantity - 1,
          cartId: cart.id,
        });
      }
    }
    return cart;
  },
);

export const removeBillingAddress = createAsyncThunk(
  'cart/removeBillingAddress',
  (_, { getState }) => {
    const { id: cartId } = getState().cart;
    return cartService.removeBillingAddress({ cartId });
  },
);

export const updateVariantLineItem = createAsyncThunk(
  'cart/updateVariantLineItem',
  ({ lineItemId, sku }, { getState }) => {
    const { id: cartId } = getState().cart;
    return cartService.updateVariantLineItem({ lineItemId, sku, cartId });
  },
);

export const getCartAOVs = createAsyncThunk('cart/getCartAOVs', () =>
  cartService.getCartAOVs(),
);

export const setShippingMethods = createAsyncThunk(
  'cart/setShippingMethods',
  ({ selectedShippingMethods }, { getState }) => {
    const { id, isDraft, store } = getState().cart;

    if (isDraft) {
      // need to add storeKey for each item in selectedShippingMethods for the draft cart endpoint
      const selectedShippingMethodsWithStoreKey = selectedShippingMethods.map(
        (shippingMethod) => ({
          ...shippingMethod,
          storeKey: store.key,
        }),
      );
      return cartMasterService.callToMethod({
        cartId: id,
        isDraft,
        methodName: cartMasterService.methodsMap.setShippingMethods,
        paramsObject: { selectedShippingMethods: selectedShippingMethodsWithStoreKey, cartId: id },
      });
    }

    return cartMasterService.callToMethod({
      cartId: id,
      isDraft,
      methodName: cartMasterService.methodsMap.setShippingMethods,
      paramsObject: { selectedShippingMethods, cartId: id },
    });
  },
);

export const setPendingSaveShippingAddress = createAction(
  'cart/setPendingSaveShippingAddress',
);
export const setIsValidatingShippingAddress = createAction(
  'cart/setIsValidatingShippingAddress',
);

export const addGiftCard = createAsyncThunk(
  'cart/addGiftCard',
  async ({ giftCardNumber, securityCode }, { getState, rejectWithValue }) => {
    const { id, isDraft } = getState().cart;

    try {
      return await cartMasterService.callToMethod({
        cartId: id,
        isDraft,
        methodName: cartMasterService.methodsMap.addGiftCard,
        paramsObject: { giftCardNumber, securityCode, cartId: id },
      });
    } catch (error) {
      return rejectWithValue({
        errorCode: error.metadata?.error_code || error.message,
      });
    }
  },
);

export const removeGiftCard = createAsyncThunk(
  'cart/removeGiftCard',
  ({ giftCardCode }, { getState }) => {
    const { id, isDraft } = getState().cart;

    return cartMasterService.callToMethod({
      cartId: id,
      isDraft,
      methodName: cartMasterService.methodsMap.removeGiftCard,
      paramsObject: { giftCardCode, cartId: id },
    });
  },
);

export const removeGiftCardsFromCart = createAsyncThunk(
  'cart/removeGiftCardsFromCart',
  (_, { getState }) => {
    const { id } = getState().cart;
    return cartService.removeGiftCardsFromCart({ cartId: id });
  },
);

export const removeGiftCardError = createAction('removeGiftCardError');
export const removeGiftCardBalance = createAction('removeGiftCardBalance');

export const getGiftCardBalance = createAsyncThunk(
  'cart/getGiftCardBalance',
  async ({ giftCardNumber, securityCode }, { getState, rejectWithValue }) => {
    const { id } = getState().cart;
    try {
      return await cartService.giftCardBalance({
        giftCardNumber,
        securityCode,
        cartId: id,
      });
    } catch (error) {
      return rejectWithValue({
        errorCode: error.metadata?.error_code || error.message,
      });
    }
  },
);

/* #region TaxEstimation */

export const removeTaxEstimation = createAction('removeTaxEstimation');
export const addTaxEstimationError = createAction('addTaxEstimationError');
export const removeTaxEstimationError = createAction(
  'removeTaxEstimationError',
);

export const getAbandonedCartToasts = createAsyncThunk(
  'getAbandonedCartToasts',
  async (params, { rejectWithValue }) => {
    try {
      const cartToasts = await cartService.getAbandonedCartToasts();
      return cartToasts;
    } catch (err) {
      return rejectWithValue({
        errorCode: err.metadata?.error_code || err.message,
      });
    }
  },
);

export const storeRecentlyAddedItems = createAction('storeRecentlyAddedItems');

export const setPromoBannerCurrentQualifiedPercentages = createAction(
  'setPromoBannerCurrentQualifiedPercentages',
);
export const setPromoBannerEstimatedQualifiedPercentages = createAction(
  'setPromoBannerEstimatedQualifiedPercentages',
);
export const setPromoBannerCurrentTier = createAction(
  'setPromoBannerCurrentTier',
);
export const setPromoBannerRemaingAmount = createAction(
  'setPromoBannerRemaingAmount',
);

/* #endregion TaxEstimation */

// Migrated to TS
export {
  setCart,
  setShippingAddressSteppedCheckout,
  setBillingAddress,
  getTaxEstimation,
  removePromoCode,
  addPromoCode,
  addShippingAddressErrorCode,
  getAvailableDiscountCodeForAbandonedCheckout,
  addLineItems,
  setShowOutOfStockToaster,
  checkLineItemsForOOSVariants,
  removeLineItem,
};

export const cartSlice = createSlice({
  name: 'cart',
  initialState: cartInStoreInitialValue,
  reducers: {
    setPendingSaveShippingAddress: (state, action) => ({
      ...state,
      pendingSaveShippingAddress: action.payload,
    }),
    setIsValidatingShippingAddress: (state, action) => ({
      ...state,
      isValidatingShippingAddress: action.payload,
    }),
  },
  extraReducers: convertToBuilderNotation({
    /* *********** */
    /* DRAFT CART */
    /* *********** */
    [getDraftCart.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [getDraftCart.fulfilled]: (state, action) => ({
      ...state,
      ...action.payload,
      isDraft: true,
      status: 'succeeded',
    }),
    [getDraftCart.rejected]: (state) => ({
      ...state,
      status: 'failed',
    }),
    /* ********* */
    /* LINEITEMS */
    /* ********* */
    [addLineItems.pending]: (state) => ({
      ...state,
      addLineItemsError: undefined,
      status: 'loading',
    }),
    [addLineItems.fulfilled]: (state, action) => ({
      ...state,
      ...action.payload,
      addLineItemsError: undefined,
      status: 'succeeded',
    }),
    [addLineItems.rejected]: (state, action) => ({
      ...state,
      status: 'failed',
      addLineItemsError: action.payload?.errorCode,
    }),
    [removeLineItem.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [removeLineItem.fulfilled]: (state, action) => ({
      ...state,
      ...action.payload,
      taxedPrice: action.payload.taxedPrice ?? undefined,
      previousLineItems: action?.payload?.lineItems || [],
      previousCustomLineItems: action?.payload?.customLineItems || [],
      status: 'succeeded',
    }),
    [removeLineItem.rejected]: (state) => ({
      ...state,
      status: 'failed',
    }),
    [changeLineItemQuantity.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [changeLineItemQuantity.fulfilled]: (state, action) => ({
      ...state,
      ...action.payload,
      previousLineItems: action?.payload?.lineItems || [],
      previousCustomLineItems: action?.payload?.customLineItems || [],
      status: 'succeeded',
    }),
    [changeLineItemQuantity.rejected]: (state) => ({
      ...state,
      status: 'failed',
    }),
    [removeDefaultGifts.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [removeDefaultGifts.fulfilled]: (state, action) => ({
      ...state,
      ...action.payload,
      previousLineItems: action?.payload?.lineItems || [],
      previousCustomLineItems: action?.payload?.customLineItems || [],
      status: 'succeeded',
    }),
    [removeDefaultGifts.rejected]: (state) => ({
      ...state,
      status: 'failed',
    }),
    [updateVariantLineItem.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [updateVariantLineItem.fulfilled]: (state, action) => ({
      ...state,
      ...action.payload,
      previousLineItems: action?.payload?.lineItems || [],
      previousCustomLineItems: action?.payload?.customLineItems || [],
      status: 'succeeded',
    }),
    [updateVariantLineItem.rejected]: (state) => ({
      ...state,
      status: 'failed',
    }),
    // Set shipping address stepped checkout
    [setShippingAddressSteppedCheckout.pending]: (state) => ({
      ...state,
      shippingAddressErrorCode: undefined,
      status: 'loading',
    }),
    [setShippingAddressSteppedCheckout.fulfilled]: (state, action) => ({
      ...state,
      ...action.payload,
      pendingSaveShippingAddress: undefined,
      shippingAddressErrorCode: undefined,
      status: 'succeeded',
    }),
    [setShippingAddressSteppedCheckout.rejected]: (state, action) => ({
      ...state,
      shippingAddressErrorCode: action.payload.shippingAddressErrorCode,
      status: 'failed',
    }),
    storeRecentlyAddedItems: (state, action) => ({
      ...state,
      recentlyAddedItems: action.payload,
    }),
    addShippingAddressErrorCode: (state, action) => ({
      ...state,
      shippingAddressErrorCode: action.payload,
    }),
    removeShippingAddressErrorCode: (state) => ({
      ...state,
      shippingAddressErrorCode: undefined,
    }),
    removeBillingAddressErrorCode: (state) => ({
      ...state,
      billingAddressErrorCode: undefined,
    }),
    /* *************** */
    /* BILLING ADDRESS */
    /* *************** */
    [setBillingAddress.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [setBillingAddress.fulfilled]: (state, action) => ({
      ...state,
      ...action.payload,
      billingAddressErrorCode: undefined,
      status: 'succeeded',
    }),
    [setBillingAddress.rejected]: (state, action) => ({
      ...state,
      billingAddress:
        action.payload.billingAddressErrorCode ===
        BILLING_ADDRESS_UNEXPECTED_ERROR
          ? undefined
          : state.billingAddress,
      billingAddressErrorCode: action.payload.billingAddressErrorCode,
      status: 'failed',
    }),
    [removeBillingAddress.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [removeBillingAddress.fulfilled]: (
      { billingAddress, ...state },
      action,
    ) => ({
      ...state,
      ...action.payload,
      status: 'succeeded',
    }),
    [removeBillingAddress.rejected]: (state) => ({
      ...state,
      status: 'failed',
    }),
    /* **************** */
    /* SHIPPING METHODS */
    /* **************** */
    [setShippingMethods.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [setShippingMethods.fulfilled]: (state, action) => ({
      ...state,
      ...action.payload,
      status: 'succeeded',
    }),
    [setShippingMethods.rejected]: (state) => ({
      ...state,
      status: 'failed',
    }),
    [addPromoCode.pending]: (state) => ({
      ...state,
      status: 'loading',
      promoErrorCode: undefined,
    }),
    [addPromoCode.fulfilled]: (state, action) => ({
      ...state,
      ...action.payload,
      status: 'succeeded',
    }),
    [addPromoCode.rejected]: (state, action) => ({
      ...state,
      status: 'failed',
      promoErrorCode: action.payload.errorCode,
    }),
    [removePromoCode.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [removePromoCode.fulfilled]: (state, action) => ({
      ...state,
      ...action.payload,
      status: 'succeeded',
    }),
    [removePromoCode.rejected]: (state) => ({
      ...state,
      status: 'failed',
    }),
    setCart: (state, data) => ({
      ...state,
      ...data.payload,
    }),
    addPromoError: (state, payload) => ({
      ...state,
      promoErrorCode: payload.errorCode,
    }),
    removePromoError: (state) => ({
      ...state,
      promoErrorCode: undefined,
    }),
    /* **************** */
    /* GIFT CARDS       */
    /* **************** */
    [addGiftCard.pending]: (state) => ({
      ...state,
      status: 'loading',
      giftCardErrorCode: undefined,
    }),
    [addGiftCard.fulfilled]: (state, action) => ({
      ...state,
      ...action.payload,
      status: 'succeeded',
    }),
    [addGiftCard.rejected]: (state, action) => ({
      ...state,
      status: 'failed',
      giftCardErrorCode: action.payload.errorCode,
    }),
    [removeGiftCard.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [removeGiftCard.fulfilled]: (state, action) => ({
      ...state,
      ...action.payload,
      status: 'succeeded',
    }),
    [removeGiftCard.rejected]: (state) => ({
      ...state,
      status: 'failed',
    }),
    [removeGiftCardsFromCart.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [removeGiftCardsFromCart.fulfilled]: (state, action) => ({
      ...state,
      ...action.payload,
      status: 'succeeded',
    }),
    [removeGiftCardsFromCart.rejected]: (state) => ({
      ...state,
      status: 'failed',
    }),
    removeGiftCardError: (state) => ({
      ...state,
      giftCardErrorCode: undefined,
    }),
    [getGiftCardBalance.pending]: (state) => ({
      ...state,
      status: 'loading',
      giftCardErrorCode: undefined,
    }),
    [getGiftCardBalance.fulfilled]: (state, action) => ({
      ...state,
      ...action.payload,
      status: 'succeeded',
      giftCardErrorCode: undefined,
    }),
    [getGiftCardBalance.rejected]: (state, action) => ({
      ...state,
      status: 'failed',
      giftCardErrorCode: action.payload.errorCode,
    }),
    removeGiftCardBalance: (state) => ({
      ...state,
      balanceAmount: undefined,
    }),
    /* ********************** */
    /* ESTIMATED TAX METHODS */
    /* ******************** */
    [getTaxEstimation.pending]: (state) => ({
      ...state,
      status: 'loading',
      estimatedTaxZipCode: undefined,
      estimatedTaxErrorCode: undefined,
    }),
    [getTaxEstimation.rejected]: (state, action) => ({
      ...state,
      status: 'failed',
      estimatedTaxErrorCode: action.payload.errorCode,
    }),
    [getTaxEstimation.fulfilled]: (state, action) => ({
      ...state,
      status: 'succeeded',
      estimatedTaxZipCode: action.payload.zipCode,
      estimatedTaxAmount: action.payload.taxAmount,
    }),
    /* ********************** */
    /* GET WARRANTY PLANS     */
    /* ********************** */
    [getWarrantyPlans.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [getWarrantyPlans.fulfilled]: (state, action) => ({
      ...state,
      warrantyPlans: action.payload || [],
      status: 'succeeded',
    }),
    [getWarrantyPlans.rejected]: (state) => ({
      ...state,
      status: 'failed',
    }),

    /* ********************** */
    /* GET PRODUCT POPULARITY */
    /* ********************** */
    [getProductPopularity.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [getProductPopularity.fulfilled]: (state, action) => ({
      ...state,
      productPopularity: action.payload || [],
      status: 'succeeded',
    }),
    [getProductPopularity.rejected]: (state) => ({
      ...state,
      productPopularity: [],
      status: 'failed',
    }),
    /* ****************************** */
    /* ADD WARRANTY PLAN TO LINE ITEM */
    /* ****************************** */
    [addWarrantyPlanToLineItem.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [addWarrantyPlanToLineItem.fulfilled]: (state, action) => ({
      ...state,
      ...action.payload,
      status: 'succeeded',
    }),
    [addWarrantyPlanToLineItem.rejected]: (state) => ({
      ...state,
      status: 'failed',
    }),
    /* ******************************** */
    /* CHECK LINEITEMS FOR OOS VARIANTS */
    /* ******************************** */
    setShowOutOfStockToaster: (state, action) => ({
      ...state,
      showOutOfStockToaster: action.payload,
    }),
    [checkLineItemsForOOSVariants.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [checkLineItemsForOOSVariants.fulfilled]: (state, action) => ({
      ...state,
      outOfStockLineItems: action.payload,
      status: 'succeeded',
    }),
    [checkLineItemsForOOSVariants.rejected]: (state) => ({
      ...state,
      outOfStockLineItems: [],
      status: 'failed',
    }),
    removeTaxEstimation: (state) => ({
      ...state,
      estimatedTaxZipCode: undefined,
      estimatedTaxAmount: undefined,
      estimatedTaxErrorCode: undefined,
    }),
    addTaxEstimationError: (state, payload) => ({
      ...state,
      estimatedTaxErrorCode: payload.errorCode,
    }),
    removeTaxEstimationError: (state) => ({
      ...state,
      estimatedTaxErrorCode: undefined,
    }),
    [getAbandonedCartToasts.pending]: (state) => ({
      ...state,
      status: 'loading',
      abandonedCartToasts: undefined,
    }),
    [getAbandonedCartToasts.rejected]: (state, action) => ({
      ...state,
      status: 'failed',
      abandonedCartToasts: action.payload,
    }),
    [getAbandonedCartToasts.fulfilled]: (state, action) => ({
      ...state,
      status: 'succeeded',
      abandonedCartToasts: action.payload,
    }),
    /* ******************************** */
    /* GET ABANDONED CARTS COUPON CODES */
    /* ******************************** */
    [getAvailableDiscountCodeForAbandonedCarts.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [getAvailableDiscountCodeForAbandonedCarts.fulfilled]: (state, action) => ({
      ...state,
      discountCodeForAbandonedCarts: action.payload,
      status: 'succeeded',
    }),
    [getAvailableDiscountCodeForAbandonedCarts.rejected]: (state) => ({
      ...state,
      discountCodeForAbandonedCarts: {},
      status: 'failed',
    }),
    [getAvailableDiscountCodeForAbandonedCheckout.pending]: (state) => ({
      ...state,
      status: 'loading',
    }),
    [getAvailableDiscountCodeForAbandonedCheckout.fulfilled]: (
      state,
      action,
    ) => ({
      ...state,
      discountCodeForAbandonedCheckout: action.payload,
      status: 'succeeded',
    }),
    [getAvailableDiscountCodeForAbandonedCheckout.rejected]: (state) => ({
      ...state,
      discountCodeForAbandonedCheckout: {},
      status: 'failed',
    }),
    /* ******************************** */
    /* BUY MORE SAVE MORE PROGRESS TRACKING */
    /* ******************************** */
    setPromoBannerCurrentQualifiedPercentages: (state, action) => ({
      ...state,
      promoBanner: {
        ...state.promoBanner,
        currentQualifiedSavings: action.payload,
      },
    }),
    setPromoBannerEstimatedQualifiedPercentages: (state, action) => ({
      ...state,
      promoBanner: {
        ...state.promoBanner,
        estimatedQualifiedSavings: action.payload,
      },
    }),
    setPromoBannerCurrentTier: (state, action) => ({
      ...state,
      promoBanner: {
        ...state.promoBanner,
        currentTier: action.payload,
      },
    }),
    setPromoBannerRemaingAmount: (state, action) => ({
      ...state,
      promoBanner: {
        ...state.promoBanner,
        remainingAmount: action.payload,
      },
    }),
    /*     [getGuaranteeItems.pending]: (state) => ({
      ...state,
      guaranteeItems: {},
      status: 'loading',
    }),
    [getGuaranteeItems.fulfilled]: (state, action) => ({
      ...state,
      guaranteeItems: action.payload,
      status: 'succeeded',
    }),
    [getGuaranteeItems.rejected]: (state) => ({
      ...state,
      guaranteeItems: {},
      status: 'failed',
    }), */
  }),
});

export default cartSlice.reducer;
